import React from 'react';
import { Box, Chip } from '@mui/material';
import { statusTone, StatusTone } from '../../theme';
import { getStatusMeta, StatusKind } from '../../utils/statusMeta';

interface StatusChipProps {
  /** What the status belongs to; picks the label map. */
  kind?: StatusKind;
  /** The raw value from the API, e.g. "CLIENT_REVIEW". */
  status?: string | null;
  /** Override the label (keeps the tone from the map unless `tone` is set). */
  label?: string;
  /** Override the tone. */
  tone?: StatusTone;
  size?: 'small' | 'medium';
}

/**
 * The one status chip for the whole app: a tinted pill with a dot, the dark
 * shade as text (about 6:1 contrast), sentence-case labels from `statusMeta`.
 * Colours are explicit so no theme default can repaint them.
 */
const StatusChip: React.FC<StatusChipProps> = ({ kind = 'project', status, label, tone, size = 'small' }) => {
  const meta = getStatusMeta(kind, status);
  const colors = statusTone[tone ?? meta.tone];
  return (
    <Chip
      size={size}
      data-tone={tone ?? meta.tone}
      label={
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
          <Box
            component="span"
            aria-hidden="true"
            sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: colors.main, flexShrink: 0 }}
          />
          {label ?? meta.label}
        </Box>
      }
      sx={{
        height: size === 'small' ? 24 : 28,
        borderRadius: '6px',
        backgroundColor: colors.bg,
        color: colors.fg,
        border: `1px solid ${colors.main}33`,
        fontWeight: 600,
        fontSize: '0.75rem',
        '& .MuiChip-label': { px: 1 },
      }}
    />
  );
};

export default StatusChip;
